/**
 * File Validation Utilities
 * Pure functions for validating file uploads
 */

/**
 * Validate file type against an accept string
 * @param {File} file - File object to validate
 * @param {string} accept - Accept string (e.g., ".csv, text/csv, image/*")
 * @returns {{ valid: boolean, error?: string }}
 */
export const validateFileType = (file, accept) => {
  if (!accept) return { valid: true };

  const acceptTypes = accept.split(',').map(t => t.trim().toLowerCase());
  const fileName = file.name.toLowerCase();
  const fileType = file.type.toLowerCase();

  const isValid = acceptTypes.some(accepted => {
    // Check extension (e.g., ".csv")
    if (accepted.startsWith('.')) {
      return fileName.endsWith(accepted);
    }
    // Check MIME type (e.g., "text/csv")
    if (accepted.includes('/')) {
      if (accepted.endsWith('/*')) {
        // Wildcard MIME (e.g., "image/*")
        const category = accepted.slice(0, -2);
        return fileType.startsWith(category);
      }
      return fileType === accepted;
    }
    return false;
  });

  return {
    valid: isValid,
    error: isValid ? null : `File type not accepted. Allowed: ${accept}`
  };
};

/**
 * Validate file size against a maximum size
 * @param {File} file - File object to validate
 * @param {number} maxSize - Maximum size in bytes
 * @returns {{ valid: boolean, error?: string }}
 */
export const validateFileSize = (file, maxSize) => {
  if (!maxSize) return { valid: true };

  const isValid = file.size <= maxSize;
  return {
    valid: isValid,
    error: isValid ? null : `File too large. Maximum size: ${formatFileSize(maxSize)}`
  };
};

/**
 * Format bytes to human-readable size string
 * @param {number} bytes - Size in bytes
 * @returns {string} Formatted size string (e.g., "1.5 MB")
 */
export const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }
  return `${size.toFixed(1)} ${units[unitIndex]}`;
};

/**
 * Validate a file against type and size constraints
 * @param {File} file - File object to validate
 * @param {{ accept?: string, maxSize?: number }} options - Validation options
 * @returns {{ valid: boolean, error?: string }}
 */
export const validateFile = (file, { accept, maxSize } = {}) => {
  // Validate type
  const typeValidation = validateFileType(file, accept);
  if (!typeValidation.valid) {
    return typeValidation;
  }

  // Validate size
  const sizeValidation = validateFileSize(file, maxSize);
  if (!sizeValidation.valid) {
    return sizeValidation;
  }

  return { valid: true };
};
